import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { DateRangePicker, createStaticRanges } from "react-date-range";
import { format, startOfYear, endOfYear, subYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subWeeks, subMonths, startOfDay } from 'date-fns';
import { enGB } from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  Modal,
} from "reactstrap";
import Select from "react-select";
import useIsTablet from "src/hooks/useIsTablet";
import { toast } from "react-toastify";
import FormattedDateInput from "./FormattedDateInput";

interface DateRangePickerComponentProps {
  date: any;
  handleDateChange: (ranges: any) => void;
  orderMinDate?: Date | null;
  orderMaxDate?: Date | null;
}

const CustomDatePicker: React.FC<DateRangePickerComponentProps> = ({ date, handleDateChange, orderMinDate, orderMaxDate }) => {
  const [openDate, setOpenDate] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [previousRange, setPreviousRange] = useState(date);

  // Apply fallback logic for min/max dates — memoized so effect deps stay stable
  const { effectiveMinDate, effectiveMaxDate } = useMemo(() => {
    const today = new Date();
    const maxDate = orderMaxDate
      ? (orderMaxDate > today ? today : orderMaxDate)
      : today;
    const minDate = orderMinDate
      ? (orderMinDate > maxDate ? maxDate : orderMinDate)
      : new Date(2016, 0, 1);
    return { effectiveMinDate: minDate, effectiveMaxDate: maxDate };
  }, [orderMinDate, orderMaxDate]);

  const handleClick = () => {
    setOpenDate(!openDate);
  };

  // Handle clicks outside the date picker
  const handleClickOutside = (event: MouseEvent) => {
    if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
      setOpenDate(false);
    }
  };

  // Add event listener for clicks outside the date picker
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Restrict month/year dropdowns to the order date range (only when order dates are set)
  useEffect(() => {
    if (!openDate || (!orderMinDate && !orderMaxDate)) return;

    const restrictMonthYearToOrderRange = () => {
      const minYear = effectiveMinDate.getFullYear();
      const minMonth = effectiveMinDate.getMonth();
      const maxYear = effectiveMaxDate.getFullYear();
      const maxMonth = effectiveMaxDate.getMonth();

      const container = datePickerRef.current || document.querySelector('.DateRangePicker');
      if (!container) return;

      // Restrict year dropdowns to order range
      container.querySelectorAll('.rdrYearPicker select').forEach((select: Element) => {
        const sel = select as HTMLSelectElement;
        Array.from(sel.options).forEach((option) => {
          const year = parseInt(option.value);
          const outOfRange = year < minYear || year > maxYear;
          option.disabled = outOfRange;
          option.style.display = outOfRange ? 'none' : '';
        });
      });

      // Restrict month dropdowns based on displayed year
      container.querySelectorAll('.rdrMonthAndYearPickers').forEach((picker: Element) => {
        const yearSelect = picker.querySelector('.rdrYearPicker select') as HTMLSelectElement;
        const monthSelect = picker.querySelector('.rdrMonthPicker select') as HTMLSelectElement;
        if (!yearSelect || !monthSelect) return;

        const displayedYear = parseInt(yearSelect.value);
        Array.from(monthSelect.options).forEach((option) => {
          const month = parseInt(option.value);
          const outOfRange =
            (displayedYear === minYear && month < minMonth) ||
            (displayedYear === maxYear && month > maxMonth);
          option.disabled = outOfRange;
          option.style.display = outOfRange ? 'none' : '';
        });
      });
    };

    // Run initially after a short delay for DOM render
    const timeoutId1 = setTimeout(restrictMonthYearToOrderRange, 50);

    // Observe changes (month/year navigation) to re-apply restrictions
    const container = datePickerRef.current || document.querySelector('.DateRangePicker');
    let observer: MutationObserver | null = null;
    if (container) {
      observer = new MutationObserver(() => {
        // Disconnect before DOM manipulation to prevent infinite loop,
        // then reconnect after changes are applied.
        observer!.disconnect();
        restrictMonthYearToOrderRange();
        observer!.observe(container, { childList: true, subtree: true });
      });
      observer.observe(container, { childList: true, subtree: true });
    }

    return () => {
      clearTimeout(timeoutId1);
      if (observer) observer.disconnect();
    };
  }, [openDate, orderMinDate, orderMaxDate, effectiveMinDate, effectiveMaxDate]);

  // Tooltip for disabled calendar days (uses event delegation — no per-element listeners needed)
  useEffect(() => {
    if (!openDate || (!orderMinDate && !orderMaxDate)) return;

    const tooltipEl = document.createElement('div');
    tooltipEl.textContent = 'Outside campaign period';
    tooltipEl.style.cssText = `
      position: fixed;
      background: var(--bs-body-bg, #fff);
      color: var(--bs-body-color, #212529);
      padding: 0.5rem 0.75rem;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      pointer-events: none;
      opacity: 0;
      z-index: 99999;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
      border: 1px solid var(--bs-border-color, rgba(0, 0, 0, 0.175));
      white-space: nowrap;
      line-height: 1.5;
      display: none;
    `;
    document.body.appendChild(tooltipEl);

    const container = datePickerRef.current || document.querySelector('.DateRangePicker');
    if (!container) { tooltipEl.remove(); return; }

    const handleMouseOver = (e: Event) => {
      const dayEl = (e.target as HTMLElement).closest('.rdrDayDisabled');
      if (!dayEl) return;
      const rect = dayEl.getBoundingClientRect();
      tooltipEl.style.left = `${rect.left + rect.width / 2}px`;
      tooltipEl.style.top = `${rect.top - 34}px`;
      tooltipEl.style.transform = 'translateX(-50%)';
      tooltipEl.style.display = 'block';
      requestAnimationFrame(() => { tooltipEl.style.opacity = '1'; });
    };

    const handleMouseOut = (e: Event) => {
      if (!(e.target as HTMLElement).closest('.rdrDayDisabled')) return;
      tooltipEl.style.opacity = '0';
      setTimeout(() => {
        if (tooltipEl.style.opacity === '0') tooltipEl.style.display = 'none';
      }, 150);
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      tooltipEl.remove();
    };
  }, [openDate, orderMinDate, orderMaxDate]);

  // Disable out-of-range static ranges with tooltip and click prevention (only when order dates are set)
  useEffect(() => {
    if (!openDate || (!orderMinDate && !orderMaxDate)) return;

    // Mark invalid static ranges as disabled
    const markDisabledRanges = () => {
      document.querySelectorAll('.rdrStaticRange').forEach((element, index) => {
        if (index >= customStaticRanges.length) return;
        const { startDate, endDate } = customStaticRanges[index].range();
        if (!isRangeValid(startDate, endDate)) {
          element.setAttribute('data-disabled', 'true');
          element.classList.add('rdrStaticRangeDisabled');
        } else {
          element.removeAttribute('data-disabled');
          element.classList.remove('rdrStaticRangeDisabled');
        }
      });
    };

    const tooltipEl = document.createElement('div');
    tooltipEl.textContent = `Outside campaign period (${format(effectiveMinDate, 'MMM dd, yyyy')} - ${format(effectiveMaxDate, 'MMM dd, yyyy')})`;
    tooltipEl.style.cssText = `
      position: fixed;
      background: var(--bs-body-bg, #fff);
      color: var(--bs-body-color, #212529);
      padding: 0.5rem 0.75rem;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      pointer-events: none;
      opacity: 0;
      z-index: 99999;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
      border: 1px solid var(--bs-border-color, rgba(0, 0, 0, 0.175));
      white-space: nowrap;
      line-height: 1.5;
      display: none;
    `;
    document.body.appendChild(tooltipEl);

    // Event delegation for tooltip + click prevention on disabled static ranges
    const container = datePickerRef.current || document.querySelector('.DateRangePicker');
    if (!container) { tooltipEl.remove(); return; }

    const handleMouseOver = (e: Event) => {
      const rangeEl = (e.target as HTMLElement).closest('.rdrStaticRange[data-disabled]');
      if (!rangeEl) return;
      const rect = rangeEl.getBoundingClientRect();
      tooltipEl.style.left = `${rect.left - 10}px`;
      tooltipEl.style.top = `${rect.top - 30}px`;
      tooltipEl.style.transform = 'none';
      tooltipEl.style.display = 'block';
      requestAnimationFrame(() => { tooltipEl.style.opacity = '1'; });
    };

    const handleMouseOut = (e: Event) => {
      if (!(e.target as HTMLElement).closest('.rdrStaticRange[data-disabled]')) return;
      tooltipEl.style.opacity = '0';
      setTimeout(() => {
        if (tooltipEl.style.opacity === '0') tooltipEl.style.display = 'none';
      }, 150);
    };

    const handleClick = (e: Event) => {
      if ((e.target as HTMLElement).closest('.rdrStaticRange[data-disabled]')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const timeoutId = setTimeout(markDisabledRanges, 100);
    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);
    container.addEventListener('click', handleClick, { capture: true });

    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      container.removeEventListener('click', handleClick, true);
      tooltipEl.remove();
    };
  }, [openDate, orderMinDate, orderMaxDate, effectiveMinDate, effectiveMaxDate]);

  // Helper function to check if a range has ANY overlap with order date range
  const isRangeValid = (startDate: Date, endDate: Date): boolean => {
    if (!orderMinDate || !orderMaxDate) return true; // No restrictions if no order dates

    // Normalize dates to start of day (midnight) to ignore time component
    const start = startOfDay(startDate);
    const end = startOfDay(endDate);
    const orderMin = startOfDay(orderMinDate);
    const orderMax = startOfDay(orderMaxDate);

    // Check for overlap: range start is before/on order end AND range end is after/on order start
    return start <= orderMax && end >= orderMin;
  };

  // Helper function to calculate intersection of static range with order date range
  const calculateIntersection = (startDate: Date, endDate: Date): { startDate: Date; endDate: Date } => {
    if (!orderMinDate || !orderMaxDate) return { startDate, endDate };

    // Calculate intersection: take the later start date and the earlier end date
    const intersectionStart = startDate > orderMinDate ? startDate : orderMinDate;
    const intersectionEnd = endDate < orderMaxDate ? endDate : orderMaxDate;

    return {
      startDate: intersectionStart,
      endDate: intersectionEnd
    };
  };

  // Helper function to create range config with validation and intersection
  const createRangeConfig = (label: string, rangeFunc: () => { startDate: Date, endDate: Date }) => {
    return {
      label,
      range: () => {
        const { startDate, endDate } = rangeFunc();
        // Return intersection with order date range
        return calculateIntersection(startDate, endDate);
      },
      isSelected: (range: any) => {
        const { startDate, endDate } = rangeFunc();
        const intersection = calculateIntersection(startDate, endDate);
        return range.startDate?.toDateString() === intersection.startDate.toDateString() &&
               range.endDate?.toDateString() === intersection.endDate.toDateString();
      }
    };
  };

  // Define custom static ranges in the desired sequence
  const customStaticRanges = createStaticRanges([
    createRangeConfig('Today', () => ({
      startDate: new Date(),
      endDate: new Date(),
    })),
    createRangeConfig('Yesterday', () => ({
      startDate: subDays(new Date(), 1),
      endDate: subDays(new Date(), 1),
    })),
    createRangeConfig('Last 7 Days', () => ({
      startDate: subDays(new Date(), 6),
      endDate: new Date(),
    })),
    createRangeConfig('Last 15 Days', () => ({
      startDate: subDays(new Date(), 14),
      endDate: new Date(),
    })),
    createRangeConfig('This Week', () => ({
      startDate: startOfWeek(new Date()),
      endDate: new Date(),
    })),
    createRangeConfig('Last Week', () => ({
      startDate: startOfWeek(subWeeks(new Date(), 1)),
      endDate: endOfWeek(subWeeks(new Date(), 1)),
    })),
    createRangeConfig('This Month', () => ({
      startDate: startOfMonth(new Date()),
      endDate: new Date(),
    })),
    createRangeConfig('Last Month', () => ({
      startDate: startOfMonth(subMonths(new Date(), 1)),
      endDate: endOfMonth(subMonths(new Date(), 1)),
    })),
    createRangeConfig('Last 30 Days', () => ({
      startDate: subDays(new Date(), 29),
      endDate: new Date(),
    })),
    createRangeConfig('This Year', () => ({
      startDate: startOfYear(new Date()),
      endDate: new Date(),
    })),
    createRangeConfig('Last Year', () => ({
      startDate: startOfYear(subYears(new Date(), 1)),
      endDate: endOfYear(subYears(new Date(), 1)),
    })),
    {
      label: 'All Data',
      range: () => ({
        startDate: effectiveMinDate,
        endDate: effectiveMaxDate,
      }),
      isSelected: (range: any) => {
        return range.startDate?.toDateString() === effectiveMinDate.toDateString() &&
               range.endDate?.toDateString() === effectiveMaxDate.toDateString();
      }
    },
  ]);

  const isStaticRange = (selected: any) =>
    customStaticRanges.some((r: any) => {
      const { startDate, endDate } = r.range();
      return (
        selected.startDate.toDateString() === startDate.toDateString() &&
        selected.endDate.toDateString() === endDate.toDateString()
      );
    });

  const isMobile = useIsTablet();

  // Portal: find the library's .rdrDateDisplayWrapper and render our inputs into it
  const [dateDisplayTarget, setDateDisplayTarget] = useState<HTMLElement | null>(null);

  const calendarRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      setDateDisplayTarget(null);
      return;
    }
    const target = node.querySelector('.rdrDateDisplayWrapper');
    setDateDisplayTarget(target as HTMLElement | null);
  }, []);

  const dateInputPortal = dateDisplayTarget
    ? createPortal(
        <div className="fdiInputRow">
          <FormattedDateInput
            value={date.startDate}
            onChange={(d) => handleDateChange({ selection: { ...date, startDate: d } })}
            minDate={effectiveMinDate}
            maxDate={date.endDate < effectiveMaxDate ? date.endDate : effectiveMaxDate}
            preventFuture
          />
          <FormattedDateInput
            value={date.endDate}
            onChange={(d) => handleDateChange({ selection: { ...date, endDate: d } })}
            minDate={date.startDate > effectiveMinDate ? date.startDate : effectiveMinDate}
            maxDate={effectiveMaxDate}
            preventFuture
          />
        </div>,
        dateDisplayTarget
      )
    : null;

  return (
    <>
      {isMobile ?
        (
          <div className="calender-container">
            <span onClick={handleClick} className="calender react-select__control">
              <span>{`${format(date.startDate, 'MMM-dd-yyyy')} To ${format(date.endDate, 'MMM-dd-yyyy')}`}</span>
              <i className="uil uil-calendar-alt"></i>
            </span>
            <Modal isOpen={openDate}  toggle ={() => setOpenDate(false)} backdrop={true} modalClassName={"general-custom-modal"}>
              <div className="modal-header">
                <h5 className="modal-title mt-0" id="myLargeModalLabel">
                  Date Range
                </h5>
                <button
                  onClick={() => {
                    setOpenDate(false);
                  }}
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true"> </span>


                </button>
              </div>
              <div className="modal-body custom-date-picker-body">
                <Select
                className="dropdown agency-select w-100"
                classNamePrefix="react-select"
                  options={customStaticRanges}
                  placeholder="Select Quick Range"
                  isOptionDisabled={(option: any) => {
                    // Disable option if it's not valid within order date range
                    const rangeFn = option.range;
                    const { startDate, endDate } = rangeFn();
                    return !isRangeValid(startDate, endDate);
                  }}
                  formatOptionLabel={(option: any) => {
                    const rangeFn = option.range;
                    const { startDate, endDate } = rangeFn();
                    const isDisabled = !isRangeValid(startDate, endDate);

                    return (
                      <div
                        className={`date-range-option${isDisabled ? ' date-range-option--disabled' : ''}`}
                        title={isDisabled ? `Outside campaign period (${format(effectiveMinDate, 'MMM dd, yyyy')} - ${format(effectiveMaxDate, 'MMM dd, yyyy')})` : ''}
                      >
                        <span>{option.label}</span>
                        {isDisabled && <span className="date-range-option__icon">⓵</span>}
                      </div>
                    );
                  }}
                  onChange={(selected: any) => {
                    if (!selected) return;
                    const rangeFn = selected.range;
                    const selectedRange = rangeFn();

                    // Validate the selected range
                    if (!isRangeValid(selectedRange.startDate, selectedRange.endDate)) {
                      toast.error(`Selected date range is outside the campaign period (${format(effectiveMinDate, 'MMM dd, yyyy')} - ${format(effectiveMaxDate, 'MMM dd, yyyy')})`);
                      return;
                    }

                    handleDateChange({ selection: selectedRange });
                    setPreviousRange(selectedRange);
                    setOpenDate(false);
                  }}
                  styles={{
                    input: (provided: any) => ({
                                  ...provided,
                                  fontSize: '16px',// stops ios zoom
                                }),
                    menu: (provided: any) => ({
                      ...provided,
                      zIndex: 10000,   // this ensures dropdown appears on top
                    }),
                    menuPortal: (provided: any) => ({
                      ...provided,
                      zIndex: 10000,
                    }),
                    option: (base: any, state: any) => ({
                                ...base,
                                backgroundColor: 'transparent',
                                color: 'inherit',
                                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                                ':active': {
                                backgroundColor: 'transparent',
                                },
                                }),
                  }}
                />
                <div ref={calendarRefCallback} className="fdiCalendarWrapper">
                  {dateInputPortal}
                  <DateRangePicker
                    className="DateRangePicker"
                    ranges={[date]}
                    months={1}
                    direction="vertical"
                    onChange={(ranges: any) => {
                      const selectedRange = ranges.selection;

                      // Validate the selected range
                      if (!isRangeValid(selectedRange.startDate, selectedRange.endDate)) {
                        toast.error(`Selected date range must be within the order period (${format(effectiveMinDate, 'MMM dd, yyyy')} - ${format(effectiveMaxDate, 'MMM dd, yyyy')})`);
                        return;
                      }

                      handleDateChange(ranges);

                      // Check if this is a static range selection by comparing with previous range
                      if (isStaticRange(selectedRange) &&
                        (selectedRange.startDate.toDateString() !== previousRange.startDate.toDateString() ||
                          selectedRange.endDate.toDateString() !== previousRange.endDate.toDateString())) {
                        // Close dropdown after a short delay to ensure the selection is processed
                        setTimeout(() => setOpenDate(false), 100);
                      }

                      setPreviousRange(selectedRange);
                    }}
                    locale={enGB}
                    minDate={effectiveMinDate}
                    maxDate={effectiveMaxDate}
                    staticRanges={[]} // empty → hide built-in sidebar
                  />
                </div>
              </div>

            </Modal>
          </div>
        ) :
        <div ref={datePickerRef} className="calender-container">
          <span onClick={handleClick} className="calender react-select__control">
            <span>{`${format(date.startDate, 'MMM-dd-yyyy')} To ${format(date.endDate, 'MMM-dd-yyyy')}`}</span>
            <i className="uil uil-calendar-alt"></i>
          </span>
          {openDate &&
            <div ref={calendarRefCallback} className="fdiCalendarWrapper">
              {dateInputPortal}
              <DateRangePicker
                className='DateRangePicker'
                ranges={[date]}
                months={2}
                direction="horizontal"
                onChange={(ranges: any) => {
                  const selectedRange = ranges.selection;

                  // Validate the selected range
                  if (!isRangeValid(selectedRange.startDate, selectedRange.endDate)) {
                    toast.error(`Selected date range must be within the order period (${format(effectiveMinDate, 'MMM dd, yyyy')} - ${format(effectiveMaxDate, 'MMM dd, yyyy')})`);
                    return;
                  }

                  handleDateChange(ranges);

                  // Check if this is a static range selection by comparing with previous range
                  if (isStaticRange(selectedRange) &&
                    (selectedRange.startDate.toDateString() !== previousRange.startDate.toDateString() ||
                      selectedRange.endDate.toDateString() !== previousRange.endDate.toDateString())) {
                    // Close dropdown after a short delay to ensure the selection is processed
                    setTimeout(() => setOpenDate(false), 100);
                  }

                  setPreviousRange(selectedRange);
                }}
                locale={enGB}
                minDate={effectiveMinDate}
                maxDate={effectiveMaxDate}
                staticRanges={customStaticRanges}
              />
            </div>}
        </div>
      }

    </>
  );
};

export default CustomDatePicker;
